import { Component } from 'solid-js'
import { userStore } from '../../../store/user'
import { Save } from 'lucide-solid'
import Button from '../../../shared/Button'
import RangeInput, { InlineRangeInput } from '../../../shared/RangeInput'
import Select from '../../../shared/Select'
import { Card } from '../../../shared/Card'
import { Typewriter } from '../../Chat/components/Message'
import { FormLabel } from '../../../shared/FormLabel'
import { Toggle } from '../../../shared/Toggle'
import { settingStore } from '../../../store/settings'
import { promptStore } from '../../../store/prompt'
import { i18nStore } from '../../../store'
import { createEmitter } from '../../../shared/util'
import { neat } from '/common/util'

export const ChatUISettings: Component = (props) => {
  const state = userStore((s) => ({ ui: s.ui }))
  const i18n = i18nStore((s) => ({ t: s.t }))

  const settings = settingStore((s) => ({ anonymize: s.anonymize }))
  const prompts = promptStore((s) => ({ hintsEnabled: s.hintsEnabled }))

  const twReset = createEmitter('reset')

  return (
    <>
      {' '}
      <Toggle
        label={i18n.t.settings_ui_responseHints}
        helperText={i18n.t.settings_ui_responseHintsHelper}
        value={prompts.hintsEnabled}
        onChange={(ev) => promptStore.toggleHints(ev)}
      />
      <Toggle
        fieldName="imageWrap"
        label={i18n.t.settings_ui_avatarWrapAround}
        helperText={i18n.t.settings_ui_avatarWrapAroundHelper}
        onChange={(value) => userStore.saveUI({ imageWrap: value })}
        value={state.ui.imageWrap}
      />
      <Toggle
        label={i18n.t.settings_ui_trimIncompleteSentences}
        fieldName="trimSentences"
        value={state.ui.trimSentences ?? false}
        onChange={(next) => userStore.saveUI({ trimSentences: next })}
      />
      <Toggle
        label={i18n.t.settings_ui_expandReasoning}
        helperText={i18n.t.settings_ui_expandReasoningHelper}
        fieldName="expandReasoning"
        value={state.ui.expandReasoning ?? false}
        onChange={(next) => userStore.saveUI({ expandReasoning: next })}
      />
      <Select
        items={[
          { value: '', label: i18n.t.settings_ui_defaultAll },
          { value: 'all', label: i18n.t.settings_ui_all },
          { value: 'post', label: i18n.t.settings_ui_afterThought },
          { value: 'pre', label: i18n.t.settings_ui_beforeThought },
        ]}
        label={i18n.t.settings_ui_midReasoningBehavior}
        helperMarkdown={i18n.t.settings_ui_midReasoningBehaviorHelper}
        value={state.ui.displayReasoning}
        onChange={(next) => userStore.saveUI({ displayReasoning: next.value as any })}
      />
      <Toggle
        value={settings.anonymize}
        label={i18n.t.settings_ui_anonymizeChat}
        helperText={i18n.t.settings_ui_anonymizeChatHelper}
        onChange={() => settingStore.toggleAnonymize()}
      />
      <Toggle
        fieldName="mobileSendOnEnter"
        label={i18n.t.settings_ui_sendOnEnterOnMobile}
        helperText={i18n.t.settings_ui_sendOnEnterOnMobileHelper}
        value={state.ui.mobileSendOnEnter}
        onChange={(ev) => userStore.saveUI({ mobileSendOnEnter: ev })}
      />
      <Card border class="!my-1 !px-2 !py-1">
        <div class="flex w-full flex-col">
          <div class="flex gap-1">
            <InlineRangeInput
              label={i18n.t.settings_ui_textSpeed}
              parentClass="w-full"
              // helperMarkdown='Speed of the "typewriter" effect when receiving new messages. Set to `0` to disable.'
              value={state.ui.textSpeed ?? 0}
              min={0}
              max={100}
              step={1}
              onChange={(ev) => {
                userStore.tryUI({ textSpeed: ev })
                twReset.emit.reset()
              }}
            />
            <Button
              size="sm"
              class="!py-2"
              onClick={() => userStore.saveUI({ textSpeed: state.ui.textSpeed })}
            >
              {i18n.t.admin_apply}
            </Button>
          </div>
          <FormLabel helperMarkdown={i18n.t.settings_ui_textSpeedHelper} />

          <Typewriter
            class="!text-700 text-sm"
            text={i18n.t.settings_ui_typewriter_test}
            speed={state.ui.textSpeed}
            reset={twReset}
          />
        </div>
      </Card>
      {/* <Toggle
        fieldName="contextWindowLine"
        label="Show context window delineator"
        helperText="Shows a dotted line above which messages are no longer inserted in the prompt"
        value={state.ui.contextWindowLine}
        onChange={(ev) => userStore.saveUI({ contextWindowLine: ev })}
        /> */}
      <Select
        fieldName="chatMode"
        label={i18n.t.settings_ui_viewMode}
        helperText={
          <>
            <b>{i18n.t.settings_ui_standard}</b>: {i18n.t.settings_ui_viewModeHelper.split('.')[0]}.
            <br />
            <b>{i18n.t.settings_ui_split}</b>: {i18n.t.settings_ui_viewModeHelper.split('.')[1]}.
            <br />
            <b>{i18n.t.settings_ui_viewMode_background}</b>: {i18n.t.settings_ui_viewModeHelper.split('.')[2]}.
          </>
        }
        items={[
          { label: i18n.t.settings_ui_standard, value: 'standard' },
          { label: i18n.t.settings_ui_split, value: 'split' },
          { label: i18n.t.settings_ui_backgroundAuto, value: 'background' },
          { label: i18n.t.settings_ui_backgroundCover, value: 'background-cover' },
          { label: i18n.t.settings_ui_backgroundContain, value: 'background-contain' },
        ]}
        value={state.ui.viewMode || 'standard'}
        onChange={(next) => userStore.saveUI({ viewMode: next.value as any })}
      />
      <div class="flex w-full items-center justify-between gap-2">
        <RangeInput
          parentClass="w-full"
          fieldName="chatModeHeight"
          min={25}
          max={65}
          step={1}
          label={i18n.t.settings_ui_splitHeight}
          helperText={i18n.t.settings_ui_splitHeightHelper}
          value={state.ui.viewHeight || 40}
          onChange={(value) => userStore.tryUI({ viewHeight: value })}
        />
        <Button onClick={() => userStore.saveUI({ viewHeight: state.ui.viewHeight || 40 })}>
          <Save />
        </Button>
      </div>
    </>
  )
}
